import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { agentApi } from "./agent.api";
import type {
	AlphaChatContinueRequest,
	AlphaChatStartRequest,
	AutomationChatLogRequest,
	SSEEvent,
} from "./agent.types";

export const useAutomationAgent = () => {
	const [isStreaming, setIsStreaming] = useState(false);
	const [events, setEvents] = useState<SSEEvent[]>([]);
	const [error, setError] = useState<Error | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);

	const startStream = useCallback((request: AlphaChatStartRequest) => {
		setIsStreaming(true);
		setEvents([]);
		setError(null);

		abortControllerRef.current = agentApi.alphaChatStart(
			request,
			(event) => {
				setEvents((prev) => [...prev, event]);
				if (event.event === "done") {
					setIsStreaming(false);
				}
			},
			(err) => {
				setError(err);
				setIsStreaming(false);
			},
		);
	}, []);

	const continueStream = useCallback((request: AlphaChatContinueRequest) => {
		setIsStreaming(true);
		setEvents([]);
		setError(null);

		abortControllerRef.current = agentApi.alphaChatContinue(
			request,
			(event) => {
				setEvents((prev) => [...prev, event]);
				if (event.event === "done") {
					setIsStreaming(false);
				}
			},
			(err) => {
				setError(err);
				setIsStreaming(false);
			},
		);
	}, []);

	const stopStream = useCallback(() => {
		abortControllerRef.current?.abort();
		setIsStreaming(false);
	}, []);

	useEffect(() => {
		return () => {
			abortControllerRef.current?.abort();
		};
	}, []);

	return {
		startStream,
		continueStream,
		stopStream,
		isStreaming,
		events,
		error,
	};
};

export const useAutomationChatLog = () => {
	return useMutation({
		mutationFn: (request: AutomationChatLogRequest) => agentApi.automationChatLog(request),
	});
};

export const useAutomationChatHistory = (
	workflowId?: string,
	limit?: number,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: ["automation-chat-history", workflowId, limit],
		queryFn: () => agentApi.automationChatHistory(workflowId, limit),
		enabled: options?.enabled ?? true,
	});
};
